<template>
  <PTransparentLoader :loading="loading">
    <div v-if="orderItem">
      <div class="d-flex align-items-center mb-3">
        <CLink
          style="color: #100F23"
          class="mr-4"
          @click="goBack"
        >
          <CIcon size="custom-size" :width="18" name="cipArrowNarrowLeft" class="mr-2"/>
          <translate translate-context="accounting.order">Back</translate>
        </CLink>
        <h1 class="main-header mb-0">
          <translate translate-context="accounting.orderDetail">Order</translate>
          {{ orderNumber }}
        </h1>
      </div>

      <!-- Switcher tra order item fratelli -->
      <div v-if="siblingItems.length > 1" class="item-switcher mb-3">
        <CButton
          v-for="sibling in siblingItems"
          :key="sibling.id"
          size="sm"
          :color="sibling.id === orderItem.id ? 'primary' : 'secondary'"
          :variant="sibling.id === orderItem.id ? '' : 'outline'"
          class="mr-2 mb-1"
          @click="switchToItem(sibling.id)"
        >
          <CIcon name="cil-camera" size="sm" class="mr-1"/>
          {{ sibling.pica_service_name || `#${sibling.id}` }}
        </CButton>
      </div>

      <!-- Sezione 1: Dettaglio articolo -->
      <div class="order-section">
        <div class="order-section__header">
          <CIcon name="cil-camera" class="mr-2"/>
          <translate translate-context="accounting.orderItem">Dettaglio articolo</translate>
        </div>
        <div class="mt-3">
          <OrderItemCard
            :item="orderItem"
            :currency="currency"
            :can-download="true"
            :can-manage-photos="canManagePhotos"
            :removed-photo-ids="removedPhotoIds"
            @download="onMediaDownload"
            @remove-photos="onRemovePhotos"
            @clear-status-photos="onClearStatusPhotos"
          />
        </div>
      </div>

      <!-- Sezione 2: Informazioni ordine (collassabile, con permesso) -->
      <div class="order-section" v-if="orderInfo && hasScopePerm('accounting.order.detail')">
        <div class="order-section__header order-section__header--clickable"
             @click="orderInfoExpanded = !orderInfoExpanded">
          <CIcon name="cil-clipboard" class="mr-2"/>
          <translate translate-context="accounting.orderItem">Informazioni ordine</translate>
          <CIcon
            :name="orderInfoExpanded ? 'cil-chevron-bottom' : 'cil-chevron-right'"
            size="sm"
            class="ml-auto"
          />
        </div>
        <CCollapse :show="orderInfoExpanded">
          <div class="mt-3">
            <OrderHeader :order="orderInfo"/>
          </div>
        </CCollapse>
      </div>

      <!-- Sezione 3: Utente -->
      <div class="order-section" v-if="userId">
        <div class="order-section__header">
          <CIcon name="cil-user" class="mr-2"/>
          <translate translate-context="accounting.orderItem">Utente</translate>
          <span class="text-muted ml-2">#{{ userId }}</span>
          <router-link
            :to="{ name: 'customer_success.user', params: { id: userId }}"
            class="reference-link ml-auto"
          >
            <CIcon name="cil-external-link" size="sm"/>
          </router-link>
        </div>
        <div class="mt-3">
          <UserInfo
            :user="userInfo"
            :user-id="userId"
            :current-source-id="orderItem.id"
            :current-checkin-picacodes="itemPicacodes"
            :scope="scope"
          />
        </div>
      </div>

      <!-- Sezione 4: Rimborso -->
      <div class="order-section order-section--no-border">
        <RefundBox
          :order-item="orderItem"
          :scope="scope"
          :removed-photos="removedPhotos"
          :purchased-count="purchasedCount"
          @clear-photos="clearPhotoSelections"
          @select-all-removed="selectAllPurchasedAsRemoved"
        />
      </div>

      <!-- Sezione 5: Suddivisione incassi (visibile a tutti) -->
      <div class="order-section" v-if="hasLedgerEntries">
        <div class="order-section__header">
          <CIcon name="cil-chart-pie" class="mr-2"/>
          <translate translate-context="accounting.orderItem">Suddivisione incassi</translate>
        </div>
        <div class="mt-3">
          <div
            v-for="(item, index) in stakeholderBreakdown"
            :key="index"
            class="breakdown-row"
          >
            <div class="d-flex justify-content-between align-items-center">
              <span>{{ item.label }}</span>
              <div class="d-flex align-items-center">
                <span class="mr-3" :class="item.amount >= 0 ? 'text-success' : 'text-danger'">
                  {{ formatAmountWithCurrency(item.amount) }}
                </span>
                <span class="text-muted" style="min-width: 45px; text-align: right;">{{ item.percentage }}%</span>
              </div>
            </div>
          </div>
          <div class="breakdown-total">
            <div class="d-flex justify-content-between align-items-center">
              <strong>
                <translate translate-context="accounting.orderItem">Totale</translate>
              </strong>
              <strong :class="totalEntriesAmount >= 0 ? 'text-success' : 'text-danger'">
                {{ formatAmountWithCurrency(totalEntriesAmount) }}
              </strong>
            </div>
          </div>
        </div>
      </div>

      <!-- Sezione 6: Movimenti contabili (solo con permesso specifico, admin) -->
      <LedgerEntriesTable :entries="orderItem.ledger_entries"
                          v-if="hasLedgerEntries && hasScopePerm('accounting.order.ledger_entries')"/>

    </div>
  </PTransparentLoader>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import LedgerEntriesTable from '../components/LedgerEntriesTable.vue'
import UserInfo from '../components/UserInfo.vue'
import OrderHeader from './components/OrderHeader.vue'
import OrderItemCard from './components/OrderItemCard.vue'
import RefundBox from './components/RefundBox.vue'
import {getRoutePrefix, STAKEHOLDER_TYPE_LABELS} from '../constants'
import ConfigScope from "@/domain/core/constant/configScope"

export default {
  name: 'OrderItemDetail',
  components: {
    PTransparentLoader,
    LedgerEntriesTable,
    UserInfo,
    OrderHeader,
    OrderItemCard,
    RefundBox
  },
  data() {
    return {
      loading: true,
      orderInfoExpanded: false,
      removedPhotos: []
    }
  },
  async mounted() {
    await this.setScope(this.scope)
    await this.fetchScopedConfig()
    await this.loadOrderItem()
  },
  computed: {
    ...mapGetters('accounting/order', ['orderItem', 'orderInfo', 'siblingItems', 'getOrderItemMedia']),

    scopeType() {
      return this.$route.meta.scopeType || 'global'
    },

    scopeId() {
      if (this.scopeType === 'pica-service') {
        return this.$route.params.picaServiceId
      } else if (this.scopeType === 'contract-manager') {
        return this.$route.params.contractManagerId
      }
      return null
    },

    scope() {
      return {
        type: this.scopeType,
        id: this.scopeId ? parseInt(this.scopeId) : null
      }
    },

    listRouteName() {
      return getRoutePrefix(this.scopeType)
    },

    orderNumber() {
      return this.orderInfo?.order_number || this.orderItem?.order_number || `#${this.orderItem?.id}`
    },

    currency() {
      return this.orderInfo?.currency || 'EUR'
    },

    userId() {
      return this.orderInfo?.user_id || null
    },

    userInfo() {
      return this.orderInfo?.user_info || null
    },

    itemPicacodes() {
      if (!this.orderItem?.picacode) return []
      return [this.orderItem.picacode]
    },

    purchasedCount() {
      if (!this.orderItem) return 0
      const mediaData = this.getOrderItemMedia(this.orderItem.id)
      return mediaData.media.filter(m => m.is_purchased).length
    },

    canManagePhotos() {
      return this.orderItem && this.orderItem.refunds !== null && this.hasScopePerm('accounting.refund.media.remove')
    },

    removedPhotoIds() {
      return this.removedPhotos.map(p => p.id)
    },

    hasLedgerEntries() {
      return this.orderItem?.ledger_entries?.length > 0
    },

    stakeholderBreakdown() {
      if (!this.hasLedgerEntries) return []
      const byStakeholder = {}
      for (const entry of this.orderItem.ledger_entries) {
        const key = entry.stakeholder_type || 'unknown'
        if (!byStakeholder[key]) {
          byStakeholder[key] = {type: key, amount: 0}
        }
        byStakeholder[key].amount += parseFloat(entry.amount) || 0
      }
      const total = Object.values(byStakeholder).reduce((sum, s) => sum + Math.abs(s.amount), 0)
      return Object.values(byStakeholder).map(s => ({
        label: STAKEHOLDER_TYPE_LABELS[s.type] || s.type,
        amount: s.amount,
        percentage: total > 0 ? ((Math.abs(s.amount) / total) * 100).toFixed(1) : '0.0'
      }))
    },

    totalEntriesAmount() {
      if (!this.hasLedgerEntries) return 0
      return this.orderItem.ledger_entries.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0)
    }
  },
  watch: {
    '$route.params.id'() {
      this.loadOrderItem()
    }
  },
  methods: {
    ...mapActions('accounting', ['setScope']),
    ...mapActions('accounting/order', ['fetchOrderItem', 'downloadMedia']),
    ...mapActions('login', ['fetchConfig']),

    hasScopePerm(permission) {
      if (!this.scope || !this.scope.type) return false

      let fullPermission
      if (this.scope.type === 'pica-service') {
        fullPermission = `pica_service.${permission}`
      } else if (this.scope.type === 'contract-manager') {
        fullPermission = `contract_manager.${permission}`
      } else if (this.scope.type === 'global') {
        fullPermission = `admin.${permission}`
      } else {
        return false
      }

      return this.hasPerm(fullPermission)
    },

    async fetchScopedConfig() {
      try {
        let configType, configId
        let configScope = null
        if (this.scopeType === 'pica-service') {
          configType = 'accounting_pica_service'
          configId = this.scopeId
          configScope = ConfigScope.PICA_SERVICE
        } else if (this.scopeType === 'contract-manager') {
          configType = 'accounting_contract_manager'
          configId = this.scopeId
        } else {
          configType = 'accounting_global'
          configId = undefined
        }
        await this.fetchConfig({type: configType, id: configId, scope: configScope})
      } catch (e) {
        // Config endpoint may not exist yet
      }
    },

    goBack() {
      const params = {}
      if (this.scopeType === 'contract-manager' && this.scopeId) {
        params.contractManagerId = this.scopeId
      } else if (this.scopeType === 'pica-service' && this.scopeId) {
        params.picaServiceId = this.scopeId
      }
      this.$router.push({name: this.listRouteName, params})
    },

    async loadOrderItem() {
      this.loading = true
      try {
        await this.fetchOrderItem(this.$route.params.id)
      } finally {
        this.loading = false
      }
    },

    switchToItem(itemId) {
      if (itemId === this.orderItem?.id) return
      const route = {
        name: this.$route.name,
        params: {...this.$route.params, id: itemId}
      }
      this.$router.push(route)
    },

    async onMediaDownload({mediaIds, orderItemId}) {
      await this.downloadMedia({orderItemId, mediaIds})
    },


    onRemovePhotos(photos) {
      for (const photo of photos) {
        if (!this.removedPhotos.find(p => p.id === photo.id)) {
          this.removedPhotos.push(photo)
        }
      }
    },

    clearPhotoSelections() {
      this.removedPhotos = []
    },

    onClearStatusPhotos(photoIds) {
      this.removedPhotos = this.removedPhotos.filter(p => !photoIds.includes(p.id))
    },

    selectAllPurchasedAsRemoved() {
      if (!this.orderItem) return
      const mediaData = this.getOrderItemMedia(this.orderItem.id)
      const purchasedPhotos = mediaData.media.filter(m => m.is_purchased)
      this.removedPhotos = purchasedPhotos
    },

    formatAmountWithCurrency(amount) {
      const num = parseFloat(amount) || 0
      return `${num.toFixed(2)} ${this.currency}`
    }
  }
}
</script>

<style scoped>
.order-section {
  padding: 20px 0;
  border-bottom: 1px solid #e4e7ea;
}

.order-section:last-of-type {
  border-bottom: none;
}

.order-section--no-border {
  border-bottom: none;
  padding-bottom: 0;
}

.order-section__header {
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  color: #333;
}

.order-section__header--clickable {
  cursor: pointer;
  user-select: none;
}

.order-section__header--clickable:hover {
  color: #000;
}

.item-switcher {
  display: flex;
  flex-wrap: wrap;
}

.reference-link {
  color: #e67e22;
  text-decoration: none;
}

.reference-link:hover {
  color: #d35400;
}

.breakdown-row {
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.breakdown-row:last-child {
  border-bottom: none;
}

.breakdown-total {
  padding: 10px 0 0;
  margin-top: 4px;
  border-top: 2px solid #e4e7ea;
}
</style>
